import type { TFunction } from 'i18next';
import type {
  Coverage,
  FollowUpItem,
  HrOverviewTeam,
  HrWorkEnvironmentOverview,
  NextDeadline,
  RiskAcrossTeams,
  RoutineLink,
  WatchItem,
} from '../../../../api/hrWorkEnvironment';
import type { MeasureDeadline } from '../../../../api/measureDeadlines';
import { WorkEnvironmentSurveyStatus } from '../../../../api/workEnvironmentTypes';
import { Routes } from '../../../../enums/Routes';
import { buildRoute } from '../../../../utils/routeBuilder';
import {
  currentStepIndex,
  dayAndMonth,
  PROCESS_STEPS,
} from '../../../WorkEnvironmentSurveyDetail/surveyProcessView';
import { fullDate, monthAndYear } from '../../hmsFormat';
import {
  EMPLOYEE_LIMITS,
  requiresSafetyRep,
} from '../../../Organizations/Business/businessView';
import {
  deadlineCounts,
  isOverdue,
  needsAttention,
} from '../MeasureDeadlines/measureDeadlinesView';

/*
 * Coverage, what must be followed up and the risk across teams are worked out in the API.
 * What is here writes it out for the overview and the pages it links to.
 */

export const COVERAGES: Coverage[] = [
  'ok',
  'inProgress',
  'dueSoon',
  'overdue',
  'never',
];

export const COVERAGE_COLORS: Record<
  Coverage,
  'success' | 'info' | 'warning' | 'danger' | 'neutral'
> = {
  ok: 'success',
  inProgress: 'info',
  dueSoon: 'warning',
  overdue: 'danger',
  never: 'neutral',
};

export const coverageCounts = (teams: HrOverviewTeam[]) =>
  COVERAGES.map((coverage) => ({
    coverage,
    count: teams.filter((team) => team.coverage === coverage).length,
  }));

const countOf = (teams: HrOverviewTeam[], coverage: Coverage) =>
  teams.filter((team) => team.coverage === coverage).length;

/** A team counts as in the process from the draft until the work is archived */
export const stepOf = (team: HrOverviewTeam) =>
  team.survey && team.survey.status !== WorkEnvironmentSurveyStatus.ARCHIVED
    ? currentStepIndex(team.survey.status)
    : null;

export const teamsInProcess = (teams: HrOverviewTeam[]) =>
  teams.filter((team) => stepOf(team) !== null);

export type Phase = 'survey' | 'risk' | 'measures' | 'followUp';

/** The nine steps in the four parts of the work */
export const PHASES: { key: Phase; steps: number[] }[] = [
  { key: 'survey', steps: [0, 1] },
  { key: 'risk', steps: [2, 3] },
  { key: 'measures', steps: [4, 5, 6] },
  { key: 'followUp', steps: [7, 8] },
];

const inSteps = (steps: number[]) => (team: HrOverviewTeam) => {
  const step = stepOf(team);
  return step !== null && steps.includes(step);
};

/** How many teams are in each part of the process, and which teams are at each step */
export const processPhases = (teams: HrOverviewTeam[]) => {
  const active = teamsInProcess(teams).length;
  return PHASES.map(({ key, steps }) => {
    const count = teams.filter(inSteps(steps)).length;
    return {
      phase: key,
      count,
      share: active > 0 ? count / active : 0,
      steps: steps.map((index) => ({
        index,
        step: PROCESS_STEPS[index],
        teams: teams.filter(inSteps([index])),
      })),
    };
  });
};

export const surveyRoute = (surveyId: number) =>
  buildRoute(Routes.HRWorkEnvironmentSurveyDetailFullPath, { surveyId });

export type RequirementItem = Extract<
  FollowUpItem,
  {
    kind:
      | 'missingRoutines'
      | 'planOverdue'
      | 'awaitingSignature'
      | 'signatureDeviation'
      | 'assessmentDeviation'
      | 'approvalDeviation';
  }
>;

type ToWatchItem = Extract<
  WatchItem,
  { kind: 'decisionSoon' | 'lowResponses' | 'tooFew' | 'mainSafetyRepOnly' }
>;

/** The rest of what must be followed up has a page of its own */
const IS_REQUIREMENT: Record<FollowUpItem['kind'], boolean> = {
  missingRoutines: true,
  planOverdue: true,
  awaitingSignature: true,
  signatureDeviation: true,
  assessmentDeviation: true,
  approvalDeviation: true,
  noSafetyRep: false,
  overdue: false,
  never: false,
};

const IS_TO_WATCH: Record<WatchItem['kind'], boolean> = {
  decisionSoon: true,
  lowResponses: true,
  tooFew: true,
  mainSafetyRepOnly: true,
  dueSoon: false,
};

export const isRequirement = (item: FollowUpItem): item is RequirementItem =>
  IS_REQUIREMENT[item.kind];

const isToWatch = (item: WatchItem): item is ToWatchItem =>
  IS_TO_WATCH[item.kind];

export const requirementItems = (overview: HrWorkEnvironmentOverview) =>
  overview.followUp.now.filter(isRequirement);

export const watchItems = (overview: HrWorkEnvironmentOverview) =>
  overview.followUp.later.filter(isToWatch);

export const teamsWithoutSafetyRep = (teams: HrOverviewTeam[]) =>
  teams.filter((team) => team.safetyReps.length === 0);

export type FixCard = {
  key: 'measures' | 'safetyReps' | 'surveys' | 'requirements';
  /** What breaks a deadline or a requirement in the law */
  count: number;
  text: string;
  route: string;
  /** The page is worth opening when there is something to fix, or to keep an eye on */
  hasLink: boolean;
};

const measuresCard = (
  measures: MeasureDeadline[],
  usesSafetyReps: boolean,
  t: TFunction,
): FixCard => {
  const { overdue, soon } = deadlineCounts(measures);
  const late = measures.filter(isOverdue);
  // A team without a safety representative has no one to tell, which the page shows on its own
  const withoutRep = late.filter((measure) => measure.safetyReps.length === 0);
  const untold = late.filter(
    (measure) =>
      measure.safetyReps.length > 0 && needsAttention(measure).safetyRep,
  ).length;
  const told =
    untold > 0
      ? t('hrOverview.fix.measures.untold', { count: untold })
      : late.length > withoutRep.length
        ? t('hrOverview.fix.measures.told')
        : '';
  const text =
    overdue === 0
      ? t(
          soon > 0
            ? 'hrOverview.fix.measures.okSoon'
            : 'hrOverview.fix.measures.ok',
          { count: soon },
        )
      : !usesSafetyReps
        ? t('hrOverview.fix.measures.remind')
        : [
          told,
          withoutRep.length > 0
            ? t('hrOverview.fix.measures.withoutRep', {
                count: withoutRep.length,
              })
            : '',
        ]
          .filter(Boolean)
          .join(' ');
  return {
    key: 'measures',
    count: overdue,
    text,
    route: Routes.HRWorkEnvironmentMeasuresFullPath,
    hasLink: overdue + soon > 0,
  };
};

const safetyRepsCard = (teams: HrOverviewTeam[], t: TFunction): FixCard => {
  const count = teamsWithoutSafetyRep(teams).length;
  return {
    key: 'safetyReps',
    count,
    text: t(
      count > 0
        ? 'hrOverview.fix.safetyReps.text'
        : 'hrOverview.fix.safetyReps.ok',
    ),
    route: Routes.HRWorkEnvironmentMissingSafetyRepsFullPath,
    hasLink: count > 0,
  };
};

/** Only the groups with teams in them are named */
const SURVEY_PARTS: Coverage[] = ['never', 'overdue', 'dueSoon'];

const surveysCard = (teams: HrOverviewTeam[], t: TFunction): FixCard => {
  const count = countOf(teams, 'overdue') + countOf(teams, 'never');
  const parts = SURVEY_PARTS.filter((coverage) => countOf(teams, coverage) > 0);
  const text = [
    count > 0 ? '' : t('hrOverview.fix.surveys.ok'),
    ...parts.map((coverage) =>
      t(`hrOverview.fix.surveys.${coverage}`, {
        count: countOf(teams, coverage),
      }),
    ),
  ]
    .filter(Boolean)
    .join(' ');
  return {
    key: 'surveys',
    count,
    text,
    route: Routes.HRWorkEnvironmentSurveysDueFullPath,
    hasLink: parts.length > 0,
  };
};

const requirementsCard = (
  overview: HrWorkEnvironmentOverview,
  t: TFunction,
): FixCard => {
  const count = requirementItems(overview).length;
  const toWatch = watchItems(overview).length;
  const text = [
    t(
      count > 0
        ? 'hrOverview.fix.requirements.text'
        : 'hrOverview.fix.requirements.ok',
    ),
    toWatch > 0 ? t('hrOverview.fix.toWatch', { count: toWatch }) : '',
  ]
    .filter(Boolean)
    .join(' ');
  return {
    key: 'requirements',
    count,
    text,
    route: Routes.HRWorkEnvironmentRequirementsFullPath,
    hasLink: count + toWatch > 0,
  };
};

/** One card for each kind of broken deadline or requirement, linking to where it is fixed */
export const fixCards = (
  overview: HrWorkEnvironmentOverview,
  measures: MeasureDeadline[],
  t: TFunction,
): FixCard[] => [
  measuresCard(measures, overview.setup.usesSafetyReps, t),
  ...(overview.setup.usesSafetyReps
    ? [safetyRepsCard(overview.teams, t)]
    : []),
  surveysCard(overview.teams, t),
  requirementsCard(overview, t),
];

export type ItemView = {
  key: string;
  kind: RequirementItem['kind'] | ToWatchItem['kind'];
  color: 'warning' | 'info';
  team: string;
  text: string;
  /** Where it is followed up */
  link: string | null;
  /** The survey whose team leaders can be reminded */
  remind: number | null;
};

const routinesKey = (links: RoutineLink[]) =>
  links.length > 1 ? 'both' : links[0];

export const requirementView = (
  item: RequirementItem,
  t: TFunction,
  language: string,
): ItemView => {
  if (item.kind === 'missingRoutines')
    return {
      kind: item.kind,
      key: 'missingRoutines',
      color: 'warning',
      team: t('hrOverview.followUp.missingRoutines.team'),
      text: t(
        `hrOverview.followUp.missingRoutines.text.${routinesKey(item.links)}`,
      ),
      link: Routes.HRRiskMatrixFullPath,
      remind: null,
    };
  const base = { kind: item.kind, team: item.team, remind: null };
  switch (item.kind) {
    case 'planOverdue':
      return {
        ...base,
        key: `planOverdue-${item.surveyId}`,
        color: 'warning',
        text: t('hrOverview.followUp.planOverdue.text', {
          count: item.areas,
          date: dayAndMonth(item.deadline, language),
        }),
        link: null,
        remind: item.surveyId,
      };
    case 'awaitingSignature':
      return {
        ...base,
        key: `awaitingSignature-${item.surveyId}`,
        color: 'warning',
        text: t('hrOverview.followUp.awaitingSignature.text', {
          count: item.days,
        }),
        link: surveyRoute(item.surveyId),
      };
    case 'signatureDeviation':
      return {
        ...base,
        key: `signatureDeviation-${item.surveyId}`,
        color: 'warning',
        text: t('hrOverview.followUp.signatureDeviation.text', {
          date: dayAndMonth(item.at, language),
          reason: item.reason ?? '',
        }),
        link: surveyRoute(item.surveyId),
      };
    case 'assessmentDeviation':
      return {
        ...base,
        key: `assessmentDeviation-${item.surveyId}`,
        color: 'warning',
        text: t(
          item.hasSafetyRep
            ? 'hrOverview.followUp.assessmentDeviation.text'
            : 'hrOverview.followUp.assessmentDeviation.textNoRep',
          { date: dayAndMonth(item.at, language), reason: item.reason ?? '' },
        ),
        link: surveyRoute(item.surveyId),
      };
    case 'approvalDeviation':
      return {
        ...base,
        key: `approvalDeviation-${item.planId}`,
        color: 'warning',
        text: t('hrOverview.followUp.approvalDeviation.text', {
          date: dayAndMonth(item.at, language),
          reason: item.reason,
        }),
        link: surveyRoute(item.surveyId),
      };
  }
};

export const watchView = (
  item: ToWatchItem,
  overview: HrWorkEnvironmentOverview,
  t: TFunction,
  language: string,
): ItemView => {
  const base = { kind: item.kind, team: item.team, remind: null };
  const min = overview.setup.minResponses;
  switch (item.kind) {
    case 'decisionSoon':
      return {
        ...base,
        key: `decisionSoon-${item.surveyId}`,
        color: 'warning',
        text: t('hrOverview.followUp.decisionSoon.text', {
          count: item.areas,
          date: dayAndMonth(item.deadline, language),
        }),
        link: surveyRoute(item.surveyId),
      };
    case 'lowResponses':
      return {
        ...base,
        key: `lowResponses-${item.surveyId}`,
        color: 'info',
        text: item.deadline
          ? t('hrOverview.followUp.lowResponses.text', {
              responses: item.responses,
              expected: item.expected,
              date: dayAndMonth(item.deadline, language),
              min,
            })
          : t('hrOverview.followUp.lowResponses.textNoDeadline', {
              responses: item.responses,
              expected: item.expected,
              min,
            }),
        link: surveyRoute(item.surveyId),
      };
    case 'tooFew':
      return {
        ...base,
        key: `tooFew-${item.teamId}`,
        color: 'info',
        text: t('hrOverview.followUp.tooFew.text', { size: item.size, min }),
        link: Routes.HRRiskMatrixFullPath,
      };
    case 'mainSafetyRepOnly':
      return {
        ...base,
        key: `mainSafetyRepOnly-${item.teamId}`,
        color: 'info',
        text: t('hrOverview.followUp.mainSafetyRepOnly.text'),
        link: Routes.HRSafetyRepsFullPath,
      };
  }
};

type Setup = HrWorkEnvironmentOverview['setup'];

const safetyRepsRequired = (setup: Setup) =>
  setup.employeeCount !== null && requiresSafetyRep(setup.employeeCount);

/** The law requires safety representatives from 5 employees, so HR confirms before turning them off */
export const confirmsTurningOff = (setup: Setup, chosen: boolean) =>
  !chosen && safetyRepsRequired(setup);

/** HR is reminded for as long as they are off where the law requires them */
export const warnsWithoutSafetyReps = (setup: Setup) =>
  !setup.usesSafetyReps && safetyRepsRequired(setup);

export const safetyRepsRequiredText = (setup: Setup, t: TFunction) =>
  t('hrOverview.setup.safetyRepsRequired', {
    employees: setup.employeeCount,
    limit: EMPLOYEE_LIMITS.safetyRep,
  });

/** Who last turned safety representatives on or off, and when */
export const safetyRepsChangedText = (
  setup: Setup,
  t: TFunction,
  language: string,
) => {
  const changed = setup.safetyRepsChanged;
  if (!changed) return null;
  const date = fullDate(changed.at, language);
  return changed.by
    ? t('hrOverview.setup.changedBy', { by: changed.by, date })
    : t('hrOverview.setup.changed', { date });
};

export type TeamFilter = 'all' | Coverage;

export const TEAM_FILTERS: TeamFilter[] = ['all', ...COVERAGES];

const matchesSearch = (team: HrOverviewTeam, search: string) => {
  const query = search.trim().toLowerCase();
  return (
    !query ||
    [team.name, ...team.leaders, ...team.safetyReps].some((value) =>
      value.toLowerCase().includes(query),
    )
  );
};

export type TeamQuery = { filter: TeamFilter; search: string };

export const filterTeams = (teams: HrOverviewTeam[], query: TeamQuery) =>
  teams.filter(
    (team) =>
      (query.filter === 'all' || team.coverage === query.filter) &&
      matchesSearch(team, query.search),
  );

export const filterCount = (teams: HrOverviewTeam[], filter: TeamFilter) =>
  filterTeams(teams, { filter, search: '' }).length;

export const stepText = (team: HrOverviewTeam, t: TFunction) => {
  const step = stepOf(team);
  return step === null
    ? '–'
    : t('hrOverview.teams.step', {
        number: step + 1,
        step: t(`workEnvironmentProcess.steps.${PROCESS_STEPS[step]}.title`),
      });
};

export const leadersText = (team: HrOverviewTeam, t: TFunction) =>
  team.leaders.join(', ') || t('hrOverview.teams.noLeader');

const NEXT_TEXT: Record<
  NextDeadline['kind'],
  (next: NextDeadline, t: TFunction, language: string) => string
> = {
  answer: (next, t, language) =>
    'date' in next && next.date
      ? t('hrOverview.teams.next.answer', {
          date: dayAndMonth(next.date, language),
        })
      : t('hrOverview.teams.next.answering'),
  plan: (next, t, language) =>
    t('hrOverview.teams.next.plan', {
      date: 'date' in next && next.date ? dayAndMonth(next.date, language) : '',
    }),
  signature: (_next, t) => t('hrOverview.teams.next.signature'),
  review: (next, t, language) =>
    t('hrOverview.teams.next.review', {
      date: 'date' in next && next.date ? dayAndMonth(next.date, language) : '',
    }),
  nextSurvey: (next, t, language) =>
    t('hrOverview.teams.next.nextSurvey', {
      date: 'date' in next && next.date ? fullDate(next.date, language) : '',
    }),
};

/** «Svarfrist 5. oktober», «Tiltaksplan innen 29. oktober» */
export const nextDeadlineText = (
  next: NextDeadline,
  t: TFunction,
  language: string,
) => NEXT_TEXT[next.kind](next, t, language);

export const nextText = (
  team: HrOverviewTeam,
  t: TFunction,
  language: string,
) =>
  team.next
    ? nextDeadlineText(team.next, t, language)
    : t('hrOverview.teams.next.none');

/** A deadline that has passed stands out */
export const nextIsLate = (team: HrOverviewTeam, now = new Date()) =>
  Boolean(
    team.next &&
      'date' in team.next &&
      team.next.date &&
      new Date(team.next.date) < now,
  );

export const teamRoute = (team: HrOverviewTeam) =>
  team.survey ? surveyRoute(team.survey.id) : null;

const DUE_COVERAGES: Coverage[] = ['overdue', 'never', 'dueSoon'];

/** The teams whose team leader should start a new survey, the most serious first */
export const surveysDue = (teams: HrOverviewTeam[]) =>
  DUE_COVERAGES.map((coverage) => ({
    coverage,
    teams: teams.filter((team) => team.coverage === coverage),
  }));

/** Since when a new survey has been due, or when it is due */
export const surveyDueText = (
  team: HrOverviewTeam,
  t: TFunction,
  language: string,
) => {
  const { next } = team;
  return team.coverage === 'overdue' && next?.kind === 'nextSurvey'
    ? t('hrOverview.surveysDue.overdueSince', {
        date: monthAndYear(next.date, language),
      })
    : nextText(team, t, language);
};

/** The factor that recurs in most teams, as a hint that the cause may be shared */
export const recurringRisk = (overview: HrWorkEnvironmentOverview) => {
  const top = overview.risks.find((risk) => risk.high + risk.moderate > 0);
  return top
    ? {
        name: top.name,
        count: top.high + top.moderate,
        total: overview.teamsWithResults,
      }
    : null;
};

/** The factors with high or moderate risk in most teams. The API sorts them. */
export const topRisks = (overview: HrWorkEnvironmentOverview, count = 3) =>
  overview.risks
    .filter((risk) => risk.high + risk.moderate > 0)
    .slice(0, count);

/** The width of a bar that shows a share */
export const percent = (share: number) => `${Math.round(share * 100)}%`;

/** How much of the bar each level fills, out of the teams that assessed risk */
export const riskShares = (
  risk: RiskAcrossTeams,
  overview: HrWorkEnvironmentOverview,
) => {
  const total = Math.max(overview.teamsWithResults, 1);
  return { high: risk.high / total, moderate: risk.moderate / total };
};
